import React from "react";
import { motion } from "framer-motion";

/*
  SeasonRibbon — a flowing "twister" gradient ribbon that sits behind the
  season text as a decorative motion accent. Built from two stacked SVG wave
  paths with a soft linear gradient + subtle drifting/swaying animation.

  Pure decoration, pointer-events disabled, never blocks text readability
  (kept at low opacity).
*/

const PALETTES = {
  yellow: { from: "#FFE066", via: "#FFC93C", to: "#FFA800" },
  orange: { from: "#FFB37A", via: "#FF7A1A", to: "#E25A00" },
  purple: { from: "#D9B8FF", via: "#A56EE0", to: "#6E2EBF" },
  blue:   { from: "#9FD6FF", via: "#4DB8FF", to: "#0E7CC9" },
};

export default function SeasonRibbon({ palette = "yellow", side = "left", id }) {
  const p = PALETTES[palette] ?? PALETTES.yellow;
  const gradId = `ribbon-grad-${id}`;
  const isRight = side === "right";

  return (
    <motion.svg
      aria-hidden
      viewBox="0 0 600 200"
      preserveAspectRatio="none"
      className="absolute pointer-events-none select-none"
      style={{
        top: "50%",
        [isRight ? "right" : "left"]: "-2%",
        transform: "translateY(-50%)",
        width: "min(56vw, 720px)",
        height: "min(28vh, 220px)",
        zIndex: 1,
        opacity: 0.55,
        mixBlendMode: "multiply",
      }}
      initial={{ opacity: 0, x: isRight ? 24 : -24 }}
      whileInView={{ opacity: 0.55, x: 0 }}
      viewport={{ once: false, amount: 0.4 }}
      transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
    >
      <defs>
        <linearGradient
          id={gradId}
          x1={isRight ? "100%" : "0%"}
          y1="0%"
          x2={isRight ? "0%" : "100%"}
          y2="100%"
        >
          <stop offset="0%"  stopColor={p.from} stopOpacity="0" />
          <stop offset="20%" stopColor={p.from} stopOpacity="0.85" />
          <stop offset="55%" stopColor={p.via}  stopOpacity="1" />
          <stop offset="85%" stopColor={p.to}   stopOpacity="0.9" />
          <stop offset="100%" stopColor={p.to}  stopOpacity="0" />
        </linearGradient>
      </defs>

      {/* Soft back ribbon — slow drift */}
      <motion.path
        d="M -40 130
           C 90 60, 200 180, 320 110
           S 540 70, 660 140
           L 660 180
           C 540 110, 420 200, 300 150
           S 90 110, -40 180 Z"
        fill={`url(#${gradId})`}
        animate={{ x: [0, 14, -8, 6, 0], y: [0, -5, 2, -3, 0] }}
        transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
        style={{ filter: "blur(0.6px)" }}
      />

      {/* Front ribbon — slightly faster, opposite drift, narrower */}
      <motion.path
        d="M -40 90
           C 110 30, 220 140, 340 70
           S 540 30, 660 100
           L 660 130
           C 540 70, 420 160, 300 110
           S 110 70, -40 140 Z"
        fill={`url(#${gradId})`}
        opacity="0.78"
        animate={{ x: [0, -10, 6, -4, 0], y: [0, 4, -3, 5, 0] }}
        transition={{ duration: 11, repeat: Infinity, ease: "easeInOut" }}
      />
    </motion.svg>
  );
}
